/**
 * Metadatos de la página desde el SPA.
 *
 * El prerender del backend (ProductoSeoController) cubre a los crawlers que NO
 * ejecutan JavaScript — que son los que arman la preview al compartir un link
 * (WhatsApp, Facebook, X). Esto cubre el otro lado: Googlebot sí ejecuta el JS,
 * y además es lo que hace que el título de la pestaña y el que ofrece
 * `navigator.share` digan de qué publicación se trata.
 */

const TITULO_DEFAULT =
  "DeAutopartes | Autopartes Usadas Originales y Recicladas | Compra Segura";
const DESCRIPCION_DEFAULT =
  "Autopartes usadas, recicladas y reutilizadas de vendedores certificados. Garantía legal, originales de desarme y envíos a todo Argentina.";

const setMeta = (selector, attr, valor) => {
  if (!valor) return;

  let tag = document.head.querySelector(selector);

  if (!tag) {
    tag = document.createElement("meta");
    const [clave, contenido] = selector
      .replace(/^meta\[|\]$/g, "")
      .split("=");
    tag.setAttribute(clave, contenido.replace(/["']/g, ""));
    document.head.appendChild(tag);
  }

  tag.setAttribute(attr, valor);
};

const setLink = (rel, href) => {
  if (!href) return;

  let tag = document.head.querySelector(`link[rel="${rel}"]`);

  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }

  tag.setAttribute("href", href);
};

/**
 * La URL canónica de la pantalla actual: el path sin query string, y `/home` como `/`.
 *
 * Sin query porque `?vendedor=3` o los parámetros que arrastran los links viejos de WhatsApp no
 * son otra página, y cada variante contaría como un duplicado. Misma regla que el script inline
 * de `public/index.html`, que la pone antes de que cargue React.
 */
export const urlCanonicaActual = () => {
  const path = window.location.pathname === "/home" ? "/" : window.location.pathname;
  return window.location.origin + path;
};

/**
 * Deja la canonical apuntando a la pantalla nueva en cada navegación.
 *
 * Antes `index.html` traía una canonical fija a la home, así que para Google `/busqueda`,
 * `/sobre-nosotros`, etc. eran copias de la home y no las indexaba. Las pantallas que tienen una
 * canonical propia (ficha, tienda, categoría) la pisan después con `actualizarMetaTags`.
 */
export const seguirCanonica = (history) => {
  setLink("canonical", urlCanonicaActual());
  return history.listen(() => setLink("canonical", urlCanonicaActual()));
};

export const actualizarMetaTags = ({ titulo, descripcion, imagen, url }) => {
  const tituloFinal = titulo ? `${titulo} | DeAutopartes` : TITULO_DEFAULT;
  const descripcionFinal = descripcion || DESCRIPCION_DEFAULT;
  const urlFinal = url || urlCanonicaActual();

  document.title = tituloFinal;

  setMeta('meta[name="description"]', "content", descripcionFinal);
  setMeta('meta[property="og:title"]', "content", tituloFinal);
  setMeta('meta[property="og:description"]', "content", descripcionFinal);
  setMeta('meta[property="og:url"]', "content", urlFinal);
  setMeta('meta[property="og:type"]', "content", titulo ? "product" : "website");
  setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
  setMeta('meta[name="twitter:title"]', "content", tituloFinal);
  setMeta('meta[name="twitter:description"]', "content", descripcionFinal);

  if (imagen) {
    setMeta('meta[property="og:image"]', "content", imagen);
    setMeta('meta[name="twitter:image"]', "content", imagen);
  }

  setLink("canonical", urlFinal);
};

/** Deja la página como estaba, para no arrastrar el producto anterior. */
export const restaurarMetaTags = () =>
  actualizarMetaTags({ titulo: undefined, descripcion: undefined });
